import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_ROLES = [
  "General Member",
  "Board Member",
  "Advisory Member",
  "Honorary Member",
  "Associate Member",
];

interface MemberRoleSelectProps {
  value: string;
  onChange: (value: string) => void;
  unaId: string;
}

export const MemberRoleSelect = ({ value, onChange, unaId }: MemberRoleSelectProps) => {
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [customRoleName, setCustomRoleName] = useState("");
  const queryClient = useQueryClient();

  // Fetch custom roles for this UNA
  const { data: customRoles = [] } = useQuery({
    queryKey: ["customMemberRoles", unaId],
    queryFn: async () => {
      if (!unaId) return [];
      
      const { data, error } = await supabase
        .from("custom_member_roles")
        .select("*")
        .eq("una_id", unaId)
        .order("role_name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  // Create custom role mutation
  const createCustomRole = useMutation({
    mutationFn: async (roleName: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("custom_member_roles")
        .insert({
          una_id: unaId,
          role_name: roleName.trim(),
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error("This role already exists");
        }
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customMemberRoles", unaId] });
      onChange(data.role_name);
      setShowCustomDialog(false);
      setCustomRoleName("");
      toast.success(`"${data.role_name}" added successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create custom role");
    },
  });

  // Delete custom role mutation
  const deleteCustomRole = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase
        .from("custom_member_roles")
        .delete()
        .eq("id", roleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customMemberRoles", unaId] });
      toast.success("Role deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete role");
    },
  });

  const handleAddCustomRole = () => {
    if (!customRoleName.trim()) {
      toast.error("Please enter a role name");
      return;
    }

    createCustomRole.mutate(customRoleName);
  };

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === "__ADD_CUSTOM__") {
      setShowCustomDialog(true);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="member_role">
            Member Role <span className="text-destructive">*</span>
          </Label>
          {customRoles.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowManageDialog(true)}
              className="h-auto p-1 text-xs hover:text-primary"
            >
              Manage Custom Roles
            </Button>
          )}
        </div>

        <Select value={value} onValueChange={handleSelectChange}>
          <SelectTrigger id="member_role">
            <SelectValue placeholder="Select member role" />
          </SelectTrigger>
          <SelectContent>
            {DEFAULT_ROLES.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}

            {customRoles.map((role) => (
              <SelectItem key={role.id} value={role.role_name}>
                {role.role_name}
              </SelectItem>
            ))}

            <SelectItem value="__ADD_CUSTOM__">
              <div className="flex items-center gap-2 text-primary">
                <Plus className="h-4 w-4" />
                Other (Add Custom Role)
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Add Custom Role Dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Member Role</DialogTitle>
            <DialogDescription>
              Create a custom role title for your UNA members. This will be saved and available for future use.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom_role_name">Role Name</Label>
              <Input
                id="custom_role_name"
                placeholder="e.g., Founding Member, Executive Member"
                value={customRoleName}
                onChange={(e) => setCustomRoleName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddCustomRole();
                  }
                }}
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCustomDialog(false);
                setCustomRoleName("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCustomRole}
              disabled={createCustomRole.isPending || !customRoleName.trim()}
            >
              {createCustomRole.isPending ? "Adding..." : "Add Role"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Custom Roles Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Custom Member Roles</DialogTitle>
            <DialogDescription>
              Delete custom roles you no longer need. Default roles cannot be deleted.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            {customRoles.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No custom roles created yet
              </p>
            ) : (
              customRoles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium">{role.role_name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Delete "${role.role_name}"? This cannot be undone.`)) {
                        deleteCustomRole.mutate(role.id);
                      }
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowManageDialog(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
